import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import React from 'react';
import CardImage from './components/CardImage.js';
import ConceptRow from './components/ConceptRow.js';
import { CardWrapper, Card as Card$1, CardContentContainer } from './Card.style.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import useIsGlobetrotter from '../../hooks/useIsGlobetrotter.js';
import useUniqueId from '../../hooks/useUniqueId.js';

var _excluded = ["children", "className", "columnSpan", "concept", "externalImage", "hideImage", "image", "imageAspectRatio", "imageOverlay", "imagePosition", "imageWidth", "lazy", "lolloBernie", "noBackground", "onClick", "onClickElementProps", "url", "variant", "footerAtBottom", "conceptRowText"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var getImageRatios = function getImageRatios(imageAspectRatio, isGlobetrotter) {
  if (!isGlobetrotter) {
    return {
      portrait: {
        aspectRatio: imageAspectRatio || 16 / 9,
        widthRatio: 1
      },
      '6-col': {
        aspectRatio: 3 / 2,
        widthRatio: 1 / 2
      },
      '4-col': {
        aspectRatio: 5 / 4,
        widthRatio: 1 / 3
      }
    };
  }
  return {
    portrait: {
      aspectRatio: imageAspectRatio || 3 / 2,
      widthRatio: 1
    },
    '6-col': {
      aspectRatio: 3 / 2,
      widthRatio: 1 / 2
    },
    '4-col': {
      aspectRatio: 4 / 3,
      widthRatio: 1 / 3
    }
  };
};

/**
 * Calculates image sizes for different breakpoints based on card configuration
 * @param {boolean} isPortraitCard - Whether the card is in portrait orientation
 * @param {number} imageWidth - Width of the image
 * @param {number} columnSpan - Number of columns the card spans (1-12)
 * @param {string} imageAspectRatio - Aspect ratio of the image
 * @param {boolean} isGlobetrotter - Whether the card is in globetrotter mode
 * @returns {Array<{width: number, height: number, ar: number, breakpoint?: number}>} Array of image sizes with dimensions and breakpoints
 * - width: Calculated width for the breakpoint
 * - height: Calculated height for the breakpoint
 * - ar: Aspect ratio for the breakpoint
 * - breakpoint: Screen width breakpoint (optional)
 */
var getImageSizes = function getImageSizes(isPortraitCard, imageWidth, columnSpan, imageAspectRatio, isGlobetrotter) {
  var columnRatio = (columnSpan || 12) / 12;
  var ratios = getImageRatios(imageAspectRatio, isGlobetrotter);
  var imageType = isPortraitCard ? 'portrait' : "".concat(imageWidth, "-col");
  var _ratios$imageType = ratios[imageType],
    aspectRatio = _ratios$imageType.aspectRatio,
    widthRatio = _ratios$imageType.widthRatio;
  var mobileAspectRatio = ratios.portrait.aspectRatio;
  return [{
    width: 1280,
    breakpoint: 975
  }, {
    width: 975,
    breakpoint: 768
  }, {
    width: 768
  }].map(function (size) {
    var isMobile = !size.breakpoint || size.breakpoint < 768;
    var ar = isMobile ? mobileAspectRatio : aspectRatio;
    var width = isMobile ? size.width : size.width * columnRatio * widthRatio;
    var height = width / ar;
    return _objectSpread(_objectSpread({}, size), {}, {
      width: Math.round(width),
      height: Math.round(height),
      ar: ar
    });
  });
};

/**
 * BaseCard component renders a card with optional image and content.
 *
 * @param {Object} props - The properties object.
 * @param {React.ReactNode} props.children - The content to be displayed inside the card.
 * @param {number} [props.columnSpan=12] - The number of columns the card spans.
 * @param {string} [props.className] - Additional CSS class names for the card.
 * @param {string} [props.concept] - The concept of the card.
 * @param {string} [props.externalImage] - URL of an external image.
 * @param {boolean} [props.hideImage=false] - Flag to hide the image.
 * @param {Image} [props.image] - The URL of the {@link Image} to be displayed in the card.
 * @param {string} [props.imageAspectRatio] - Aspect ratio of the image.
 * @param {React.ReactNode} [props.imageOverlay] - Overlay for the image.
 * @param {string} [props.imagePosition='left'] - Position of the image ('left' or 'top').
 * @param {number} [props.imageWidth=6] - Width of the image in columns.
 * @param {boolean} [props.lazy=false] - Flag to enable lazy loading for the image.
 * @param {boolean} [props.lolloBernie] - Additional image property.
 * @param {boolean} [props.noBackground] - Flag to remove background.
 * @param {Function} [props.onClick] - Click handler for the card.
 * @param {Object} [props.onClickElementProps] - Additional properties for clickable elements.
 * @param {string} [props.url] - URL for the card link.
 * @param {string} [props.variant] - Variant of the card.
 * @param {Object} [props.rest] - Additional properties.
 * @param {boolean} [props.footerAtBottom=false] - Flag to position footer at the bottom.
 * @param {string} props.conceptRowText - The text to be displayed in the concept row.
 *
 * @returns {JSX.Element} The rendered BaseCard component.
 */
var BaseCard = function BaseCard(_ref) {
  var children = _ref.children,
    className = _ref.className,
    _ref$columnSpan = _ref.columnSpan,
    columnSpan = _ref$columnSpan === void 0 ? 12 : _ref$columnSpan,
    concept = _ref.concept,
    externalImage = _ref.externalImage,
    _ref$hideImage = _ref.hideImage,
    hideImage = _ref$hideImage === void 0 ? false : _ref$hideImage,
    image = _ref.image,
    imageAspectRatio = _ref.imageAspectRatio,
    imageOverlay = _ref.imageOverlay,
    _ref$imagePosition = _ref.imagePosition,
    imagePosition = _ref$imagePosition === void 0 ? 'left' : _ref$imagePosition,
    _ref$imageWidth = _ref.imageWidth,
    imageWidth = _ref$imageWidth === void 0 ? 6 : _ref$imageWidth,
    _ref$lazy = _ref.lazy,
    lazy = _ref$lazy === void 0 ? false : _ref$lazy,
    lolloBernie = _ref.lolloBernie,
    noBackground = _ref.noBackground,
    onClick = _ref.onClick,
    onClickElementProps = _ref.onClickElementProps,
    url = _ref.url,
    variant = _ref.variant,
    footerAtBottom = _ref.footerAtBottom,
    conceptRowText = _ref.conceptRowText,
    rest = _objectWithoutProperties(_ref, _excluded);
  var isGlobetrotter = useIsGlobetrotter();
  var isPortraitCard = imagePosition === 'top';
  var cardSettings = {
    $columnSpan: columnSpan,
    $imagePosition: imagePosition,
    $imageWidth: imageWidth,
    $isPortraitCard: isPortraitCard,
    $noBackground: noBackground,
    $variant: variant,
    $footerAtBottom: footerAtBottom || isGlobetrotter || variant !== 'hotel'
  };

  /**
   * Handles the click event.
   * If an onClick function is provided, it will be called.
   *
   * @returns {boolean} Always returns true.
   */
  var handleClick = function handleClick() {
    if (onClick) {
      onClick();
    }
    return true;
  };
  return /*#__PURE__*/jsxs(CardWrapper, _objectSpread(_objectSpread({
    className: className
  }, cardSettings), {}, {
    children: [!isGlobetrotter && conceptRowText && /*#__PURE__*/jsx(ConceptRow, {
      conceptRowText: conceptRowText
    }), /*#__PURE__*/jsxs(Card$1, _objectSpread(_objectSpread(_objectSpread({}, rest), cardSettings), {}, {
      children: [!hideImage && /*#__PURE__*/jsx(CardImage, {
        cardSettings: {
          $imagePosition: cardSettings.$imagePosition
        },
        concept: concept,
        externalImage: externalImage,
        image: image,
        lazy: lazy,
        imageOverlay: imageOverlay,
        lolloBernie: lolloBernie,
        onClick: handleClick,
        sizes: getImageSizes(isPortraitCard, imageWidth, columnSpan, imageAspectRatio, isGlobetrotter)
      }), /*#__PURE__*/jsx(CardContentContainer, _objectSpread(_objectSpread({}, cardSettings), {}, {
        children: React.Children.map(children, function (child) {
          if (/*#__PURE__*/React.isValidElement(child)) {
            return /*#__PURE__*/React.cloneElement(child, _objectSpread(_objectSpread({
              key: child.key || useUniqueId('card-content')
            }, child.props), {}, {
              url: url,
              onClick: onClick ? handleClick : undefined,
              onClickElementProps: onClickElementProps,
              cardSettings: cardSettings
            }));
          }
          return child;
        })
      }))]
    }))]
  }));
};
var Card = BaseCard;

export { Card as default };
//# sourceMappingURL=Card.js.map
